/**
 * Logout Guard — Feb 2026
 *
 * Purpose: Suppress error toasts ("Gagal memuat dashboard" etc) that fire when
 * background/polling requests get rejected during logout. Without this, axios
 * polls fire after `localStorage.removeItem('token')` → backend returns 401 →
 * each dashboard's catch block shows a misleading error toast.
 *
 * Design:
 *   - A shared AbortController is attached to every axios request (via
 *     interceptor in AuthContext). On logout we call .abort() to cancel all
 *     in-flight requests, then replace the controller so future requests
 *     (e.g. login page) get a fresh one.
 *   - `isLoggingOut` is a short-lived flag that stays true from the moment
 *     logout starts until ~1s after redirect. Any error during that window
 *     is "silent".
 *   - `isSilentError(err)` is the single check dashboards use before showing
 *     their fetch error toast.
 *   - As a global safety-net, this module also monkey-patches sonner's
 *     `toast.error` once on first import so that any toast.error invocation
 *     anywhere in the app becomes a no-op while logout is in progress.
 *     Normal (non-logout) behavior is unchanged.
 *
 * NO auth, token storage, or redirect logic lives here.
 */

import axios from 'axios';
import { toast } from 'sonner';

let _isLoggingOut = false;
let _controller = new AbortController();

export function getLogoutAbortSignal() {
  return _controller.signal;
}

export function isLogoutInProgress() {
  return _isLoggingOut;
}

/**
 * Called at the very start of logout(). Aborts pending requests and
 * replaces the controller so subsequent requests (login etc.) aren't
 * pre-aborted. The flag is cleared automatically after a short delay.
 */
export function beginLogout() {
  _isLoggingOut = true;
  try {
    _controller.abort();
  } catch (_e) {
    // ignore
  }
  _controller = new AbortController();
  // Clear the flag shortly after — long enough for any straggling
  // post-token-clear requests to settle, short enough not to silence
  // a real session-expired toast on the next page.
  setTimeout(() => {
    _isLoggingOut = false;
  }, 1500);
}

/**
 * Returns true if the given error should be silently swallowed (no toast,
 * no console noise). True when:
 *   - request was canceled via AbortController (axios.isCancel)
 *   - logout is currently in progress
 *   - error was tagged earlier by the response interceptor
 *   - request returned 401 but token is already gone from storage (i.e. the
 *     request was inflight when logout cleared the token)
 */
export function isSilentError(error) {
  if (!error) return false;
  if (_isLoggingOut) return true;
  if (error.isLogoutAbort) return true;
  try {
    if (axios.isCancel && axios.isCancel(error)) return true;
  } catch (_e) {
    // ignore
  }
  if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError' || error.name === 'AbortError') {
    return true;
  }
  if (error.response?.status === 401 && !localStorage.getItem('token')) {
    return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Global safety-net: monkey-patch sonner's `toast.error` once.
// Any `toast.error(...)` invocation while logout is in progress becomes a
// no-op. Outside the logout window, behaviour is unchanged.
// ---------------------------------------------------------------------------
if (typeof toast === 'function' || typeof toast === 'object') {
  if (toast && typeof toast.error === 'function' && !toast.__logoutGuardPatched) {
    const _originalError = toast.error.bind(toast);
    toast.error = (...args) => {
      if (_isLoggingOut) return undefined;
      return _originalError(...args);
    };
    // Marker so we never double-patch on hot reloads.
    try {
      Object.defineProperty(toast, '__logoutGuardPatched', {
        value: true,
        enumerable: false,
        configurable: false,
        writable: false,
      });
    } catch (_e) {
      // ignore — patch still applied even if marker fails
    }
  }
}

